import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ServicesTab from "../components/ServicesTab";
import seoData from "../assets/data/seo.json";
import { Helmet } from "react-helmet-async";
import LogoLoader from "../components/LogoLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faAnchor, faShip } from "@fortawesome/free-solid-svg-icons";
import {
  Globe,
  HomeHero,
  HomeHeroVideo,
  HomeImage01,
  AboutHeritage,
  VisakhapatnamImg,
  HaldiaImg
} from "../assets/data/Imagedata";

const { title, description, keywords, canonical, ogImage } = seoData.home;

const sections = [
  {
    title: <>ABOUT OUR <span className="text-primary italic">LEGACY</span></>,
    subtitle: "LIMITLESS AMBITION",
    number: "01",
    description:
      "Evolving from local roots to a globally integrated force, our 30-year journey is defined by unwavering purpose and operational excellence.",
    image: AboutHeritage,
    buttons: "Know More",
    link: "/aboutus/",
  },
  {
    title: <>OPERATIONAL <span className="text-primary italic">PRIDE</span></>,
    subtitle: "GLOBAL EXCELLENCE",
    number: "02",
    description:
      "Streamlined maritime operations across worldwide with cutting-edge logistics and technology integration.",
    image: VisakhapatnamImg,
    buttons: "More Info",
    link: "/operations/",

  },
  {
    title: <>YOUR <span className="text-primary italic">QUERIES</span></>,
    subtitle: "GET ANSWERS",
    number: "03",
    description:
      "Find quick answers to common questions about our services, operations, and partnership opportunities.",
    image: HaldiaImg,
    buttons: "FAQs",
    link: "/frequently-asked-questions/",
  },
];

const Home = () => {
  const [isSticky, setIsSticky] = useState(false);
  const firstSectionRef = useRef(null);
  const navRef = useRef(null);
  const [firstSectionHeight, setFirstSectionHeight] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (navRef.current) {
        // Use a small buffer to detect when the element hits the top
        const rect = navRef.current.getBoundingClientRect();
        setIsSticky(rect.top <= 0);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2500);
    return () => clearTimeout(timer);
  }, []);

  if (loading) return <LogoLoader isLoading={loading} />;

  return (
    <div className="bg-white min-h-screen">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="keywords" content={keywords} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <link rel="canonical" href={canonical} />
      </Helmet>

      {/* First Section */}
      <section
        ref={firstSectionRef}
        className="relative min-h-[70vh] flex flex-col "
      >
        {/* Main Content with Background Image */}
        <div className="flex-grow flex items-center justify-center relative overflow-hidden">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="absolute inset-0 w-full h-full object-cover"
          >
            <source src={HomeHeroVideo} type="video/mp4" />
          </video>
          {/* Dark overlay for better text readability*/}
          <div className="absolute inset-0 md:bg-black/0 bg-black/40" />

          <div className="relative z-10 container mx-auto px-4 py-8 md:py-12">
            <div className="flex flex-col md:flex-row items-center justify-between gap-8">
              {/* Logo and Content */}
              <div className="w-full md:w-1/2">
                {/* Headings */}
                <div className="mb-4">
                  {/* Mobile Heading */}
                  <h2 className="md:hidden text-[34px] font-TuskerGrotesk font-bold text-white leading-[1.1] mb-2 uppercase tracking-wider">
                    NAVIGATING <span className="text-primary italic">SATISFACTION</span>
                  </h2>

                  {/* Desktop/Laptop Heading */}
                  <h2 className="hidden md:block text-[64px] font-TuskerGrotesk font-bold text-white leading-[1.1] mb-2 uppercase tracking-wider">
                    NAVIGATING CLIENT
                    <br />
                    <span className="text-primary italic">SATISFACTION</span>
                  </h2>
                </div>

                {/* Tagline */}
                <div className="mb-6">
                  <p className="text-[16px] text-white/90">
                    End-to-End Supply Chain Excellence
                  </p>
                </div>

                {/* Button */}
                <div className="mt-6">
                  <Link
                    to="/services/"
                    className="group inline-flex items-center justify-center px-8 py-3 text-[14px] font-bold text-black bg-white hover:bg-primary hover:text-white rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_0_20px_rgba(253,89,1,0.4)]"
                  >
                    <span className="flex items-center gap-3">
                      <FontAwesomeIcon icon={faShip} className="text-[18px]" />
                      DISCOVER OUR SERVICES
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div
        ref={navRef}
        className=" w-[50vw] mx-auto sticky top-0 z-[3000] transition-all duration-300"
      >
        <div className={` px-4 hidden md:block mx-auto transition-all duration-500 ${isSticky ? "" : "bg-primary rounded-b-full"}`}>
          <div className="flex container mx-auto justify-around ">
            {/* ABOUT */}
            <div>
              <Link
                to="/aboutus/"
                className="block py-3 md:py-4 text-center group"
              >
                <h2 className="text-[16px] font-semibold text-white tracking-widest uppercase group-hover:text-primary transition-colors">
                  COMPANY
                </h2>
              </Link>
            </div>
            <div className="border-r-[0.5px]" />

            {/* GROUPS */}
            <div>
              <Link to="/services/" className="block py-3 md:py-4 text-center group">
                <h2 className="text-[16px] font-semibold text-white tracking-widest uppercase group-hover:text-primary transition-colors">
                  SERVICES
                </h2>
              </Link>
            </div>
            <div className="border-r-[0.5px]" />

            {/* ESG */}
            <div>
              <Link
                to="/operations/"
                className="block py-3 md:py-4 text-center group"
              >
                <h2 className="text-[16px] font-semibold text-white tracking-widest uppercase group-hover:text-primary transition-colors">
                  OPERATIONS
                </h2>
              </Link>
            </div>
            <div className="border-r-[0.5px]" />

            {/* TEAM */}
            <div>
              <Link to="/contactus/" className="block py-3 md:py-4 text-center group">
                <h2 className="text-[16px] font-semibold text-white tracking-widest uppercase group-hover:text-primary transition-colors">
                  CONTACT
                </h2>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <section className="py-12 md:py-20 bg-white relative overflow-hidden">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-20">
            {/* Left Column: Text Content */}
            <div className="w-full md:w-3/4">
              {/* Heading */}
              <div className="mb-6 md:mb-8">
                <h2 className="text-[18px] md:text-[24px] text-black uppercase leading-[1.1] tracking-wider">WHY CHOOSE</h2>
                <h2 className="text-[34px] md:text-[36px] font-TuskerGrotesk font-bold text-black uppercase leading-[1.1] tracking-wider">
                  <span className="text-primary italic">Shivam Shipping & Logistics-SCM ?</span>
                </h2>
              </div>

              {/* Content */}
              <div className="mb-8 md:mb-10">
                <div className="space-y-4 text-slate-600">
                  <p className="text-[18px] md:text-[18px] font-medium leading-relaxed italic border-l-4 border-primary/20 pl-6">
                    Backed by 30 years of experience in shipping and logistics, our proprietor is dedicated to delivering exceptional solutions that streamline<br /> global trade.
                  </p>
                  <p className="text-[16px] leading-relaxed text-slate-500">
                    We uniquely combine deep local expertise in Paradip with world-class logistics standards.
                  </p>
                </div>
              </div>

              <Link
                to="/services/"
                className="group inline-flex items-center justify-center px-8 py-3 text-[14px] font-bold text-white bg-black border border-white/10 hover:bg-primary hover:text-white rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgba(0,0,0,0.1)]"
              >
                <span className="flex items-center gap-3">
                  <FontAwesomeIcon icon={faShip} className="text-[18px]" />
                  Check More
                  <span className="group-hover:translate-x-1 transition-transform">→</span>
                </span>
              </Link>
            </div>

            {/* Right Column: Image */}
            <div className="w-full md:w-2/5 relativ">
              <div className="relative z-10  transform transition-transform duration-700 hover:scale-[1.02]">
                <img
                  src={HomeImage01}
                  alt="Shipping Excellence"
                  className="w-full h-auto object-cover animate-pulse"
                />
                <div className="absolute inset-0" />
              </div>
            </div>
          </div>
        </div>
      </section>



      {/*Expertise section*/}
      <section
        className="relative min-h-[60vh] w-full flex flex-col justify-center items-center overflow-hidden font-sans"
        style={{
          backgroundImage: `url(${Globe})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        {/* Enhanced Background Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-transparent to-black/30"></div>

        {/* Top Content Row */}
        <div className="absolute top-0 z-10 w-full flex flex-col md:flex-row justify-between items-start md:items-start p-8 md:p-12 lg:p-16">
          <div className="space-y-4 md:space-y-6 mb-6 md:mb-0">
            <div className="flex items-center space-x-4"></div>
            <h2 className="text-white text-[34px] font-TuskerGrotesk font-bold uppercase leading-tight tracking-widest">
              GLOBAL <span className="text-primary italic">OPERATIONS</span>
            </h2>
          </div>


        </div>

        {/* CENTERED Globe and Expertise Section */}
        <div className="relative z-20 w-full flex flex-col items-center justify-center flex-grow">
          {/* Globe Container */}
          <div className="relative w-[250px] h-[250px] md:w-[450px] md:h-[450px] lg:w-[550px] lg:h-[550px] opacity-80 mb-8 md:mb-12">
            {/* Pulsing rings effect */}
            <div className="absolute inset-0 rounded-full border-2 border-white/20 animate-ping"></div>
            <div className="absolute inset-8 rounded-full border border-white/10"></div>

            {/* Globe image or placeholder */}
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="w-full h-full rounded-full bg-gradient-to-br from-blue-900/20 to-cyan-900/10 border border-white/10 flex items-center justify-center">
                <div className="text-center">
                  <div className="text-white/60 text-[12px] font-semibold uppercase tracking-widest mb-1">
                    Global Network
                  </div>
                  <div className="text-white/40 text-[34px] font-bold">
                    8
                  </div>
                  <div className="text-white/50 text-[10px]">LOCATIONS</div>
                </div>

                {/* Animated dots representing locations */}
                {[...Array(8)].map((_, i) => (
                  <div
                    key={i}
                    className="absolute w-3 h-3 bg-white rounded-full animate-pulse"
                    style={{
                      top: `${Math.sin((i * 45 * Math.PI) / 180) * 40 + 50}%`,
                      left: `${Math.cos((i * 45 * Math.PI) / 180) * 40 + 50}%`,
                      animationDelay: `${i * 0.2}s`,
                    }}
                  ></div>
                ))}
              </div>
            </div>
          </div>

          {/* CENTERED "EXPERTISE" Typography */}
          <div className="relative w-full flex justify-center items-center">
            <div className="relative">
              {/* Shadow/Outline effect */}
              <div className="absolute inset-0 text-transparent">
                <h1 className="text-[34px] font-TuskerGrotesk font-bold leading-none opacity-30">
                  EXPERTISE
                </h1>
              </div>



              {/* Subtitle under expertise */}
              <div className="absolute -bottom-6 md:-bottom-8 left-1/2 transform -translate-x-1/2 text-center">
                <p className="text-white/60 text-[12px] font-light tracking-widest uppercase">
                  Since 1999
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Decorative corner accents */}
        <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-white/10"></div>
        <div className="absolute top-0 right-0 w-32 h-32 border-t-2 border-r-2 border-white/10"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 border-b-2 border-l-2 border-white/10"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-white/10"></div>
      </section>

      <ServicesTab />

      {/*Cards Section */}
      <div className="relative">
        {sections.map((sec, index) => (
          <div
            key={index}
            className="h-[80vh] sticky top-[10vh] flex items-center justify-center overflow-hidden"
          >
            {/* Background Image */}
            <div
              className="absolute inset-0 bg-cover bg-center bg-no-repeat transition-transform duration-1000"
              style={{ backgroundImage: `url('${sec.image}')` }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/60"></div>
            </div>

            {/* Content */}
            <div className="relative z-10 w-full max-w-6xl mx-auto px-4 md:px-8">
              <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Left Column */}
                <div>
                  {/* Section Number */}
                  <div className="text-[34px] font-bold text-white/10 mb-2 md:mb-4 tracking-tighter">
                    {sec.number}
                  </div>

                  {/* Title & Subtitle */}
                  <div className="mb-4 md:mb-6">
                    <div className="text-white/80 text-[14px] font-semibold tracking-widest uppercase mb-1">
                      {sec.subtitle}
                    </div>
                    <h1 className="text-[34px] font-TuskerGrotesk font-bold text-white leading-[1.1] uppercase">
                      {sec.title}
                    </h1>
                  </div>

                  {/* Description */}
                  <p className="text-[16px] text-white/90 mb-6 md:mb-8 leading-relaxed max-w-lg">
                    {sec.description}
                  </p>

                  <Link
                    to={sec.link}
                    className="group inline-flex items-center justify-center px-8 py-3 text-[14px] font-bold text-black bg-white hover:bg-primary hover:text-white rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-xl"
                  >
                    <span className="flex items-center gap-3">
                      <FontAwesomeIcon icon={faShip} className="text-[18px]" />
                      {sec.buttons}
                      <span className="group-hover:translate-x-1 transition-transform">→</span>
                    </span>
                  </Link>

                </div>

                {/* Right Column - Image Preview */}
                <div className="hidden md:block">
                  <div className="relative">
                    <div className="aspect-square rounded-2xl overflow-hidden shadow-2xl border-4 border-white/10">
                      <img
                        src={sec.image}
                        alt={sec.title}
                        className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    {/* Decorative Elements */}
                    <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-white/20 rounded-xl"></div>
                    <div className="absolute -bottom-4 -left-4 w-16 h-16 border border-white/20 rounded-xl"></div>
                  </div>
                </div>
              </div>

              {/* Indicator */}
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2">
                <div className="flex items-center space-x-4">
                  <div className="text-white/70 text-[12px]">
                    {index + 1} / {sections.length}
                  </div>
                  <div className="flex space-x-2">
                    {sections.map((_, i) => (
                      <div
                        key={i}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${i === index ? "bg-white w-8" : "bg-white/30"
                          }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Scroll hint for first section */}
            {index === 0 && (
              <div className="z-50 absolute bottom-12 left-1/2 transform -translate-x-1/2 animate-bounce">
                <div className="text-white text-center">
                  <div className="text-sm mb-2 opacity-70">
                    Scroll to explore
                  </div>
                  <div className="w-6 h-10 border-2 border-white/50 rounded-full mx-auto flex justify-center">
                    <div className="w-1 h-3 bg-white rounded-full mt-2"></div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Closing Decoration */}
      <footer className="py-12 bg-slate-50 border-t border-slate-200">
        <div className="container mx-auto px-4 text-center">
          <div className="w-px h-16 bg-gradient-to-b from-primary to-transparent mx-auto mb-8"></div>
          <FontAwesomeIcon icon={faAnchor} className="text-primary/20 text-4xl mb-6 animate-pulse" />
          <h2 className="text-[18px] font-bold text-slate-400 uppercase tracking-[0.5rem] mb-2">ESTD 1996</h2>
          <p className="text-slate-400 text-[10px] font-bold tracking-[0.3rem] uppercase leading-none">Shivam Shipping and Logistics-SCM</p>
        </div>
      </footer>
    </div>
  );
};

export default Home;
